"use client";

import { useState, type ChangeEventHandler } from "react";
import { useId } from "react";
import {
	type Control,
	type FieldPath,
	type FieldValues,
	useController,
} from "react-hook-form";

import { uploadReviewVideo, uploadReviewVideoThumbnail } from "@/lib/api/s3";
import { generateReviewVideoThumbnailFile } from "@/lib/media/review-video-thumbnail";
import { sanitizeUserFacingErrorMessage, cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

type Props<T extends FieldValues> = {
	name: FieldPath<T>;
	control: Control<T>;
	entityId?: string;
	onThumbnailUploaded?: (thumbnailUrl: string) => void;
	variant?: "default" | "glass";
	/** When true, upload UI fills the parent height (review dialog toggle slot). */
	fillContainer?: boolean;
};

export default function VideoUploadInput<T extends FieldValues>({
	control,
	name,
	entityId,
	onThumbnailUploaded,
	variant = "default",
	fillContainer = false,
}: Props<T>) {
	const { field } = useController({ control, name });
	const inputId = useId();
	const { toast } = useToast();
	const [isUploading, setIsUploading] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);

	const onChange: ChangeEventHandler<HTMLInputElement> = async ({ target }) => {
		if (!target || !target.files || target.files.length === 0) return;
		const file = target.files[0];

		// Validate file type
		if (!file.type.startsWith("video/")) {
			toast({
				variant: "destructive",
				title: "Invalid file type",
				description: "Please select a video file.",
			});
			target.value = "";
			return;
		}

		// Validate file size (max 1GB for videos)
		const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB in bytes
		if (file.size > MAX_FILE_SIZE) {
			toast({
				variant: "destructive",
				title: "File too large",
				description: `The video size (${(file.size / 1024 / 1024 / 1024).toFixed(2)}GB) exceeds the maximum limit of 1GB. Please select a smaller video.`,
			});
			target.value = "";
			return;
		}

		setSelectedFile(file);
		setIsUploading(true);

		try {
			const videoUrl = await uploadReviewVideo(file, entityId);
			field.onChange(videoUrl);
			let thumbnailFailed = false;
			if (onThumbnailUploaded) {
				try {
					const thumbnailFile = await generateReviewVideoThumbnailFile(file);
					const thumbnailUrl = await uploadReviewVideoThumbnail(thumbnailFile, entityId);
					onThumbnailUploaded(thumbnailUrl);
				} catch (thumbnailError) {
					console.warn("Error generating review video thumbnail:", thumbnailError);
					onThumbnailUploaded("");
					thumbnailFailed = true;
				}
			}
			toast({
				title: "Video uploaded successfully",
				description: thumbnailFailed
					? "Your video uploaded, but the preview thumbnail could not be created."
					: "Your review video has been uploaded.",
			});
		} catch (error) {
			console.error("Error uploading video:", error);
			toast({
				variant: "destructive",
				title: "Upload failed",
				description:
					error instanceof Error
						? sanitizeUserFacingErrorMessage(error.message, "Failed to upload video. Please try again.")
						: "Failed to upload video. Please try again.",
			});
			field.onChange("");
			onThumbnailUploaded?.("");
			setSelectedFile(null);
		} finally {
			setIsUploading(false);
			target.value = "";
		}
	};

	const handleRemove = () => {
		field.onChange("");
		onThumbnailUploaded?.("");
		setSelectedFile(null);
	};

	const videoUrl = field.value;
	const isGlass = variant === "glass";
	const dropzoneClassName = isGlass
		? cn(
				"flex w-full min-w-0 cursor-pointer flex-col items-center justify-center rounded-md border border-secondary/35 bg-white/85 shadow-sm backdrop-blur-2xl transition-colors hover:border-secondary hover:bg-secondary/10 dark:bg-background/25",
				fillContainer ? "h-full p-4" : "p-8",
			)
		: "flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 transition-colors hover:border-gray-400 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700";
	const uploadingClassName = isGlass
		? cn(
				"flex items-center justify-center rounded-md border border-secondary/35 bg-white/85 shadow-sm backdrop-blur-2xl dark:bg-background/25",
				fillContainer ? "h-full p-4" : "p-8",
			)
		: "flex items-center justify-center rounded-lg border border-gray-300 bg-gray-50 p-8 dark:border-gray-700 dark:bg-gray-800";
	const previewClassName = isGlass
		? cn(
				"relative rounded-md border border-secondary/35 bg-white/85 p-4 shadow-sm backdrop-blur-2xl dark:bg-background/25",
				fillContainer && "h-full overflow-y-auto",
			)
		: "relative rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800";
	const iconClassName = isGlass
		? cn("stroke-secondary", fillContainer ? "size-8" : "size-10")
		: "h-12 w-12 text-gray-400";
	const titleClassName = isGlass
		? "text-xs font-medium text-foreground"
		: "text-sm font-medium text-gray-700 dark:text-gray-300";
	const hintClassName = isGlass
		? "text-xs text-muted-foreground"
		: "text-xs text-gray-500 dark:text-gray-400";

	return (
		<div className={cn(isGlass && "w-full min-w-0", fillContainer ? "h-full" : "space-y-3")}>
			{!videoUrl && !selectedFile && (
				<label htmlFor={inputId} className={dropzoneClassName}>
					<input
						id={inputId}
						name={String(name)}
						type="file"
						accept="video/*"
						hidden
						onChange={onChange}
						disabled={isUploading}
						aria-label="Upload review video"
					/>
					<div className="flex flex-col items-center gap-2">
						<svg
							className={iconClassName}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							aria-hidden
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
							/>
						</svg>
						<div className="text-center">
							<p className={titleClassName}>
								{isUploading ? "Uploading video..." : "Click to upload video"}
							</p>
							<p className={hintClassName}>MP4, MOV, AVI (up to 1GB)</p>
						</div>
					</div>
				</label>
			)}

			{isUploading && (
				<div className={uploadingClassName}>
					<div className="flex flex-col items-center gap-3">
						<LoadingSpinner />
						<p className={cn(isGlass ? "text-xs text-muted-foreground" : "text-sm text-gray-600 dark:text-gray-400")}>
							Uploading {selectedFile?.name}...
						</p>
					</div>
				</div>
			)}

			{videoUrl && !isUploading && (
				<div className={previewClassName}>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="absolute right-2 top-2 h-8 w-8"
						onClick={handleRemove}
						aria-label="Remove video"
					>
						<X className="h-4 w-4" />
					</Button>
					<div className="pr-10">
						<p className={cn("mb-2", titleClassName)}>Video uploaded successfully</p>
						<video
							src={videoUrl}
							controls
							className="w-full rounded-lg"
							preload="metadata"
						>
							Your browser does not support the video tag.
						</video>
					</div>
				</div>
			)}
		</div>
	);
}
